import axios from 'axios';
import { useAuthStore } from '@/context/authStore';

// Cliente público para la tienda online: sin interceptores de login, para que un
// cliente cualquiera pueda navegarla sin cuenta.
const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
});

// Si quien mira tiene sesión abierta en el sistema, mandamos el token igual. Es lo
// que habilita la VISTA PREVIA: una tienda sin publicar responde 404 a todo el
// mundo menos a la gente del propio local. Para un cliente sin cuenta no cambia
// nada, porque no hay token que mandar.
publicApi.interceptors.request.use((config) => {
  try {
    const token = useAuthStore.getState().token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    /* la tienda tiene que funcionar aunque no exista sesión */
  }
  return config;
});

// Base para servir imágenes (/uploads). El backend las sirve en /uploads; en prod nginx
// proxea /uploads al backend y en dev se apunta directo al host del backend.
export const UPLOADS_BASE = (import.meta.env.VITE_API_URL || '').replace('/api/v1', '');

export interface StoreInfo {
  published?: boolean;
  slug: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
  whatsapp?: string | null;
  transfer_info?: string | null;
}

export interface StoreProduct {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  image_url?: string | null;
  display_unit: string;
  dimension: string;
  category_name?: string | null;
  is_combo: boolean;
  components?: { name: string; quantity: number; unit?: string | null }[];
  available: boolean;
  stock_available?: number | null;
}

export interface StoreCategory {
  id: number;
  name: string;
}

export const storeService = {
  async getStore(slug: string): Promise<StoreInfo> {
    const { data } = await publicApi.get<StoreInfo>(`/public/store/${slug}`);
    return data;
  },
  async getProducts(slug: string, params?: { category_id?: number; search?: string }): Promise<StoreProduct[]> {
    const { data } = await publicApi.get<StoreProduct[]>(`/public/store/${slug}/products`, { params });
    return data;
  },
  async getCategories(slug: string): Promise<StoreCategory[]> {
    const { data } = await publicApi.get<StoreCategory[]>(`/public/store/${slug}/categories`);
    return data;
  },
  async createOrder(slug: string, payload: {
    customer_name: string;
    customer_phone: string;
    customer_address?: string;
    payment_method: string;
    delivery_method: string;
    notes?: string;
    items: { product_id: number; quantity: number }[];
  }): Promise<{ id: number; order_number: string; total: number; status: string }> {
    const { data } = await publicApi.post(`/public/store/${slug}/orders`, payload);
    return data;
  },
};

// URL completa de una imagen de producto
export const productImageUrl = (image_url?: string | null): string | undefined =>
  image_url ? `${UPLOADS_BASE}${image_url}` : undefined;
